/**
 * Módulo 8 — Subastas.
 *
 * Modela el árbol completo que devuelve `GET /auctions/:id/full`:
 *   subasta (vehículo) → piezas solicitadas → ofertas de vendedores,
 * cada nivel con sus fotos. Los DECIMAL de MySQL llegan como string, por eso
 * los importes se tipan como `number | string` y se normalizan con `toNumber`.
 */

/** Foto de subasta, pieza solicitada u oferta (todas comparten forma). */
export interface AuctionImage {
  id: number;
  imageUrl: string;
  sortOrder?: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  auctionId?: number;
  requestedPartId?: number;
  offerId?: number;
}

/** Vendedor embebido en cada oferta. */
export interface OfferUser {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  roleId?: number | null;
  status?: string | null;
  rating?: number | string | null;
  userPhoto?: string | null;
  isActive?: boolean;
}

/** Desglose de precio que ve el comprador (solo en `GET /auctions/offers/:id`). */
export interface OfferPricing {
  currency: string;
  piece: number | null;
  appFeePercent: number;
  appFee: number | null;
  pieceTotal: number | null;
  shipping: number | null;
  total: number | null;
}

/** Estimado de envío DHL por oferta; `available: false` trae el motivo. */
export interface OfferShippingEstimate {
  available: boolean;
  reason?: string;
  detail?: string;
  productCode?: string;
  productName?: string;
  priceCents?: number;
  currency?: string;
}

/**
 * Desglose con el que se cobró una oferta ya pagada. Viene de la fila de
 * `payments`, así que refleja las comisiones vigentes ESE día: cambiar los
 * porcentajes en Configuración no lo altera.
 */
export interface OfferSettlement {
  buyerFee: number | null;
  buyerFeePercent: number | null;
  sellerCommission: number | null;
  sellerCommissionPercent: number | null;
  sellerPayout: number | null;
}

/**
 * Compatibilidad del vehículo donante con el de la solicitud. La calcula el
 * backend, no el cliente: `exact` = mismo año/marca/modelo, `listed` = aparece
 * en las compatibilidades generadas por la IA para la pieza, `unknown` =
 * ninguna de las dos. `unknown` es una PISTA para el comprador, no un rechazo.
 */
export type OfferVehicleCompatibility = 'exact' | 'listed' | 'unknown';

/** Vehículo del que proviene la pieza ofertada (`partVehicle`). */
export interface OfferPartVehicle {
  yearId?: number | null;
  year?: number | string | null;
  makeId?: number | null;
  make?: string | null;
  modelId?: number | null;
  model?: string | null;
  /** Versión / acabado libre (LE, Sport…). */
  version?: string | null;
  /** Características libres ("Hatchback, motor 2.0"). */
  specification?: string | null;
  compatibility?: OfferVehicleCompatibility | null;
}

/** Oferta de un vendedor sobre una pieza solicitada. */
export interface AuctionOffer {
  id: number;
  requestedPartId: number;
  userId?: number;
  partConditionId?: number | null;
  partCondition?: { id: number; name: string } | null;
  /** Precio base del vendedor (alias de `price` en el árbol completo). */
  priceStart?: number | string | null;
  price?: number | string | null;
  /** Precio que paga el comprador: base + la comisión del comprador vigente. */
  priceEnd?: number | string | null;
  currency?: string | null;
  exchangeRate?: number | string | null;
  comments?: string | null;
  locationId?: number | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  deliveryType?: number | null;
  deliveryEtaDays?: number | null;
  status?: number | string | null;
  statusLabel?: string | null;
  moderationStatus?: string | null;
  moderationReason?: string | null;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  user?: OfferUser;
  photos?: AuctionImage[];
  pricing?: OfferPricing | null;
  /** Desglose real si ya se pagó; `null` mientras la oferta sigue viva. */
  settled?: OfferSettlement | null;
  shippingEstimate?: OfferShippingEstimate | null;
  partName?: string | null;
  sellerName?: string | null;
  /** Vehículo donante; `null` en ofertas creadas antes de esta función. */
  partVehicle?: OfferPartVehicle | null;
}

/** Estimado de empaque calculado por la IA (forma DHL) para una pieza. */
export interface PartAiEstimate {
  package?: {
    typeCode?: string | null;
    weight?: number | null;
    dimensions?: {
      length?: number | null;
      width?: number | null;
      height?: number | null;
    } | null;
  } | null;
  unitOfMeasurement?: string | null;
  confidence?: number | null;
  source?: string | null;
}

/**
 * Solicitud de pieza: la unidad que caduca. El vehículo que la contiene es
 * permanente, así que `endsAt` e `isOpen` de aquí son los que mandan.
 */
export interface AuctionRequestedPart {
  id: number;
  auctionId: number;
  partName?: string | null;
  description?: string | null;
  desiredState?: string | null;
  aiEstimate?: PartAiEstimate | null;
  requestedAt?: string;
  endsAt?: string | null;
  closedAt?: string | null;
  status?: number | string | null;
  awardedOfferId?: number | null;
  awardedAt?: string | null;
  /** Solicitud de la que se clonó al pulsar "volver a solicitar". */
  renewedFromId?: number | null;
  /** Resuelto por el backend: sigue abierta y dentro de su vigencia. */
  isOpen?: boolean;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  photos?: AuctionImage[];
  offers?: AuctionOffer[];
}

/** Snapshot del vehículo capturado por el comprador (forma libre). */
export type VehicleSnapshot = Record<string, unknown>;

/**
 * Vehículo del comprador para el que se piden piezas (tabla `auctions`).
 *
 * Es permanente: se guarda una vez y se reutiliza. `endsAt` quedó como dato
 * histórico de cuando el vehículo caducaba; para saber si hoy está vivo en el
 * mercado se usan `openPartsCount` y `nextPartEndsAt`.
 */
export interface Auction {
  id: number;
  catalogId?: number;
  yearId?: number;
  year?: number | string;
  makeId?: number;
  make?: string;
  modelId?: number;
  model?: string;
  userId?: number;
  identificationHash?: string | null;
  specification?: string | null;
  vehicleSnapshot?: VehicleSnapshot | null;
  startsAt?: string;
  /** @deprecated Histórico. El vehículo ya no caduca; usa `nextPartEndsAt`. */
  endsAt?: string | null;
  status?: number | string;
  /** Archivado por su dueño: sigue existiendo pero no se lista. */
  archivedAt?: string | null;
  /** Última vez que se pidió una pieza para este vehículo. */
  lastRequestAt?: string | null;
  /** Solicitudes abiertas y vigentes. 0 ⇒ vehículo guardado, invisible para vendedores. */
  openPartsCount?: number;
  /** Vencimiento de la solicitud viva más próxima; `null` si no hay ninguna. */
  nextPartEndsAt?: string | null;
  /** Todas las solicitudes que ha tenido, vigentes o no. */
  totalPartsCount?: number;
  isActive?: boolean;
  deletedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  photos?: AuctionImage[];
}

/** Árbol completo de `GET /auctions/:id/full`. */
export interface AuctionFull extends Auction {
  photos: AuctionImage[];
  requestedParts: AuctionRequestedPart[];
}

/** Condición de la pieza (`GET /auctions/part-conditions`). */
export interface PartCondition {
  id: number;
  name: string;
  isActive?: boolean;
}

/**
 * Fase del vehículo. Ya no la marcan fechas propias (el vehículo no caduca)
 * sino si tiene solicitudes vivas: `requesting` es lo que ven los vendedores,
 * `idle` es un coche guardado esperando a que su dueño pida otra pieza.
 */
export type AuctionPhase = 'inactive' | 'archived' | 'requesting' | 'idle';

export const AUCTION_PHASE_LABELS: Record<AuctionPhase, string> = {
  inactive: 'Inactivo',
  archived: 'Archivado',
  requesting: 'Con solicitudes',
  idle: 'Guardado',
};

/** Clave canónica para `<app-status-badge>` según la fase. */
export const AUCTION_PHASE_BADGE: Record<AuctionPhase, string> = {
  inactive: 'inactive',
  archived: 'closed',
  requesting: 'active',
  idle: 'pending',
};

/** `requested_parts.status` (TINYINT) → clave canónica del badge. */
export const REQUESTED_PART_STATUS: Record<string, string> = {
  '1': 'open',
  '2': 'awarded',
  '3': 'paid',
  '4': 'cancelled',
  '5': 'expired',
};

/** `offers.status` (TINYINT) → clave canónica del badge. */
export const OFFER_STATUS: Record<string, string> = {
  '1': 'active',
  '2': 'winner',
  '3': 'paid',
  '4': 'rejected',
};

/** Etiqueta en español de la compatibilidad del vehículo donante. */
export const COMPATIBILITY_LABELS: Record<OfferVehicleCompatibility, string> = {
  exact: 'Mismo vehículo',
  listed: 'Compatible según IA',
  unknown: 'Verificar compatibilidad',
};

/** Clave canónica para `<app-status-badge>` según la compatibilidad. */
export const COMPATIBILITY_BADGE: Record<OfferVehicleCompatibility, string> = {
  exact: 'compat_exact',
  listed: 'compat_listed',
  unknown: 'compat_unknown',
};

/** Texto de apoyo que explica cada compatibilidad en el detalle de la oferta. */
export const COMPATIBILITY_HINTS: Record<OfferVehicleCompatibility, string> = {
  exact: 'Mismo vehículo que la solicitud.',
  listed: 'La IA lo marcó como compatible con la solicitud.',
  unknown: 'Vehículo distinto al de la solicitud; el comprador debe verificar compatibilidad.',
};

/** Motivos por los que no hay estimado de envío para una oferta. */
export const SHIPPING_UNAVAILABLE_REASONS: Record<string, string> = {
  part_not_found: 'La pieza ya no existe.',
  no_ai_estimate: 'La pieza no tiene estimado de empaque (peso/medidas).',
  buyer_no_address: 'El comprador no tiene domicilio registrado.',
  offer_no_postal_code: 'La oferta no tiene código postal del vendedor.',
  no_domestic_product: 'DHL no ofrece servicio nacional para esa ruta.',
  dhl_error: 'DHL no respondió la cotización.',
};

/** Normaliza importes/porcentajes que MySQL devuelve como string. */
export function toNumber(value: number | string | null | undefined): number {
  if (value === null || value === undefined || value === '') return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Precio base del vendedor de una oferta. */
export function offerBasePrice(o: AuctionOffer): number {
  return toNumber(o.priceStart ?? o.price);
}

/**
 * Precio que paga el comprador. Lo calcula el backend con la comisión vigente
 * y viaja en `priceEnd`; si faltara, se devuelve la base sin inventar recargo.
 */
export function offerFinalPrice(o: AuctionOffer): number {
  return toNumber(o.priceEnd) || offerBasePrice(o);
}

/** Redondeo a dos decimales para importes derivados. */
export function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Nombre presentable del vendedor de una oferta. */
export function offerSellerName(o: AuctionOffer): string {
  const full = [o.user?.firstName, o.user?.lastName].filter(Boolean).join(' ').trim();
  return o.sellerName || full || o.user?.email || (o.userId != null ? `#${o.userId}` : '—');
}

/** Título del vehículo donante: "año marca modelo versión" (o '—' sin datos). */
export function partVehicleTitle(v: OfferPartVehicle | null | undefined): string {
  if (!v) return '—';
  const title = [v.year, v.make, v.model, v.version]
    .map((part) => (part == null ? '' : String(part).trim()))
    .filter(Boolean)
    .join(' ');
  return title || '—';
}

/** Compatibilidad que manda el backend; `null` si falta o no se reconoce. */
export function offerCompatibility(
  v: OfferPartVehicle | null | undefined,
): OfferVehicleCompatibility | null {
  const raw = v?.compatibility;
  return raw && raw in COMPATIBILITY_LABELS ? raw : null;
}

/** `vehicleSnapshot.version` (LT, Limited, Sport…) recortada; `null` si no existe. */
export function vehicleSnapshotVersion(
  snapshot: VehicleSnapshot | null | undefined,
): string | null {
  const raw = snapshot?.['version'];
  if (typeof raw !== 'string' && typeof raw !== 'number') return null;
  const version = String(raw).trim();
  return version || null;
}

/**
 * Fase del vehículo según `isActive`, `archivedAt` y sus solicitudes vivas.
 *
 * `nextPartEndsAt` se comprueba además del contador para no marcar como activo
 * un vehículo cuya última solicitud venció entre la consulta y el render.
 */
export function auctionPhase(a: Auction | null | undefined, now: number = Date.now()): AuctionPhase {
  if (!a) return 'idle';
  if (a.isActive === false) return 'inactive';
  if (a.archivedAt) return 'archived';
  const nextEnd = a.nextPartEndsAt ? Date.parse(a.nextPartEndsAt) : NaN;
  if ((a.openPartsCount ?? 0) > 0 && (!Number.isFinite(nextEnd) || nextEnd > now)) return 'requesting';
  return 'idle';
}
